import { useQuery } from "@tanstack/react-query";
import useApiClient from "../useApiClient";

type PagedAdminQueryOptions = {
  database?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
  // Page size — must be <= the server-side row cap. Defaults to 9000 (cap is
  // ~10000) so the "last page is short" termination check works reliably.
  pageSize?: number;
  // Safety stop. If we somehow ask for more than this many rows, bail.
  maxRows?: number;
};

type AdminQueryResult = {
  columns: string[];
  rows: unknown[][];
  rowCount: number;
  truncated: boolean;
  durationMs: number;
};

// Paged variant of useAdminQuery. The caller's SQL MUST end with a stable
// ORDER BY (deterministic ordering is what makes paging safe) and MUST NOT
// already contain LIMIT or OFFSET — we append them. Loops until the server
// returns fewer than pageSize rows, then concatenates everything into a single
// result shaped exactly like useAdminQuery's payload.
const usePagedAdminQuery = (
  sql: string,
  parameters: unknown[] = [],
  options?: PagedAdminQueryOptions,
) => {
  const api = useApiClient();
  const database = options?.database;
  const enabled = options?.enabled ?? true;
  const refetchInterval = options?.refetchInterval;
  const pageSize = options?.pageSize ?? 9000;
  const maxRows = options?.maxRows ?? 200_000;

  return useQuery<AdminQueryResult>({
    queryKey: ["paged-admin-query", sql, parameters, database, pageSize],
    enabled,
    refetchInterval,
    queryFn: async () => {
      let columns: string[] = [];
      const allRows: unknown[][] = [];
      let offset = 0;
      let totalDuration = 0;
      let lastTruncated = false;
      while (true) {
        const pagedSql = `${sql}\nLIMIT ${pageSize} OFFSET ${offset}`;
        const { data, error } = await api.POST("/admin/query", {
          body: { sql: pagedSql, parameters, database },
        });
        if (error) throw error;
        if (!data) throw new Error("No data");
        columns = data.columns;
        totalDuration += data.durationMs ?? 0;
        lastTruncated = data.truncated;
        for (const r of data.rows as unknown[][]) allRows.push(r);
        if (data.rows.length < pageSize) break;
        offset += pageSize;
        if (offset >= maxRows) {
          throw new Error(`Paged query exceeded maxRows safety limit (${maxRows})`);
        }
      }
      return {
        columns,
        rows: allRows,
        rowCount: allRows.length,
        // We've fully drained the result, so the merged payload is never
        // truncated. Pass through the last page's flag for diagnostic purposes.
        truncated: lastTruncated && allRows.length === 0,
        durationMs: totalDuration,
      };
    },
  });
};

export default usePagedAdminQuery;
